import { useReduxState } from 'hooks';
import { ProgressBar } from 'components/common';
import { Icon } from 'components/common/icons';

import type { IconProp } from 'components/common/icons/Icon';

interface Props {
  category?: 'default' | 'success' | 'danger' | 'warning';
  icon?: IconProp;
  label?: string;
  progress?: number;
  progressShowIfZero?: boolean;
  progressValueLabel?: string;
  tooltip?: string;
}

const CardProgressStatus = ({
  category = 'default',
  icon,
  label,
  progress,
  progressShowIfZero,
  progressValueLabel,
  tooltip
}: Props) => {
  const { theme } = useReduxState();

  return (
    <div
      className="status"
      {...(tooltip
        ? {
            'aria-label': tooltip,
            'data-tooltip': tooltip
          }
        : {})}
    >
      {icon ? (
        <div className="value">
          <div className={`value-icon value-icon-${category}`}>
            <Icon icon={icon} />
          </div>
          <div className={`value-label value-label-${category}`}>{label}</div>
        </div>
      ) : progress || (progress !== undefined && progressShowIfZero) ? (
        <div className="progress">
          <div className="progress-header">
            <div className="progress-label">{label}</div>
            {progressValueLabel ? (
              <div className="progress-value">{progressValueLabel}</div>
            ) : null}
          </div>
          <div className="progress-bar">
            <ProgressBar
              color={theme.textColorLight}
              progress={progress || 0}
            />
          </div>
        </div>
      ) : null}

      <style jsx>{`
        .value {
          align-items: center;
          display: flex;
          gap: 7px;
        }
        .value-icon {
          background-color: ${theme.lightGrey};
          border-radius: 26px;
          color: ${theme.textColor};
          flex-shrink: 0;
          font-size: 1rem;
          padding: 7px 0;
          text-align: center;
          height: 26px;
          width: 26px;
        }
        .value-icon-danger {
          background-color: ${theme.dangerLightColor};
          color: ${theme.dangerLightTextColor};
        }
        .value-icon-success {
          background-color: ${theme.successLightColor};
          color: ${theme.successLightTextColor};
        }
        .value-icon-warning {
          background-color: ${theme.warningLightColor};
          color: ${theme.warningLightTextColor};
        }
        .value-label {
          color: ${theme.textColorLight};
          font-size: 1.2rem;
          font-weight: 600;
        }
        .value-label-danger {
          color: ${theme.dangerColor};
        }
        .value-label-success {
          color: ${theme.successStandaloneColor};
        }
        .value-label-warning {
          color: ${theme.textColor};
        }
        .progress {
          min-width: 120px;
        }
        .progress-header {
          color: ${theme.textColorLight};
          display: flex;
          font-size: 1.2rem;
          font-weight: 600;
          gap: 10px;
          justify-content: space-between;
        }
        .progress-bar {
          margin-top: 6px;
        }
      `}</style>
    </div>
  );
};

export default CardProgressStatus;
